import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { assignableRoles, defaultRoleFor, ROLE_LABELS } from "@/lib/roles";

/**
 * Single dialog for both Invite (no `user` prop) and Edit (with `user` prop).
 * - Invite: full form (name + email + role + languages)
 * - Edit:   name + role + languages; email is immutable (would break login)
 */
export default function UserDialog({ open, onOpenChange, user: target, onSaved }) {
  const { user: caller } = useAuth();
  const { active } = useClient();
  const clientRoles = active?.roles || null;
  const defaultRole = defaultRoleFor(clientRoles);
  const isEdit = !!target;
  const [form, setForm] = useState({
    email: "", name: "", role: defaultRole,
    workstation_kind: "none", preset_id: "",
    languages: ["english"],
  });
  const [submitting, setSubmitting] = useState(false);
  const [presets, setPresets] = useState([]);

  useEffect(() => {
    if (target) {
      setForm({
        email: target.email,
        name: target.name,
        role: target.role,
        workstation_kind: target.workstation_kind || "none",
        preset_id: "",
        languages: (target.languages && target.languages.length > 0)
          ? target.languages : ["english"],
      });
    } else {
      setForm({
        email: "", name: "", role: defaultRole,
        workstation_kind: "none", preset_id: "",
        languages: ["english"],
      });
    }
  }, [target, open, defaultRole]);

  // Load presets for the invite flow (starting template)
  useEffect(() => {
    if (open && !isEdit && presets.length === 0) {
      api.get("/permission-presets").then((r) => setPresets(r.data || [])).catch(() => {});
    }
  }, [open, isEdit]);

  const roles = assignableRoles(caller, clientRoles);
  // When editing, preserve target's current role even if above caller's hierarchy
  // (the API will still 403 on dangerous promotions, but display matters).
  const availableRoles = isEdit && !roles.includes(target.role)
    ? [target.role, ...roles]
    : roles;

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    if (!isEdit && !form.email?.trim()) return toast.error("Email is required");
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = {};
        if (form.name !== target.name) payload.name = form.name;
        if (form.role !== target.role) payload.role = form.role;
        const currWk = target.workstation_kind || "none";
        if (form.workstation_kind !== currWk) payload.workstation_kind = form.workstation_kind;
        // languages diff — compare sorted arrays
        const before = [...(target.languages || ["english"])].sort().join(",");
        const after = [...(form.languages || [])].sort().join(",");
        if (before !== after) payload.languages = form.languages;
        if (Object.keys(payload).length === 0) {
          onOpenChange(false);
          return;
        }
        await api.patch(`/users/${target.id}`, payload);
        toast.success("User updated");
      } else {
        // Invite: always send languages so the agent is in the correct routing pool from day 1.
        await api.post("/users", { ...form, languages: form.languages });
        toast.success(`Invite sent · ${form.email}`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? "Update failed" : "Invite failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={isEdit ? "edit-user-dialog" : "invite-user-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit team member" : "Invite team member"}
          </DialogTitle>
          {isEdit && (
            <DialogDescription className="text-xs text-slate-500">
              Email is permanent — to change it, soft-delete this user and invite a new one.
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={isEdit}
              data-testid="user-email-input"
              className={isEdit ? "bg-slate-50" : ""}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => (
                  <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isEdit && form.role !== target.role && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1.5">
                Changing <strong>{target.name}</strong>&apos;s role from <strong>{ROLE_LABELS[target.role]}</strong> → <strong>{ROLE_LABELS[form.role]}</strong>. They&apos;ll keep or lose permissions on their next API call.
              </p>
            )}
          </div>

          {/* Languages — Arabic pool eligibility for Ooredoo routing (§4.7). */}
          <div className="space-y-1.5">
            <Label>
              Spoken languages
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-normal ml-1.5">
                for lead call routing
              </span>
            </Label>
            <div className="flex flex-wrap gap-3">
              {["english", "arabic"].map((lang) => {
                const checked = (form.languages || []).includes(lang);
                return (
                  <label
                    key={lang}
                    className="flex items-center gap-2 text-xs cursor-pointer select-none px-2.5 py-1.5 rounded border border-slate-200 bg-white hover:border-indigo-300 transition-colors"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        setForm((f) => {
                          const langs = new Set(f.languages || []);
                          if (v) langs.add(lang); else langs.delete(lang);
                          // Guarantee at least one language stays selected.
                          if (langs.size === 0) langs.add("english");
                          return { ...f, languages: Array.from(langs) };
                        });
                      }}
                      data-testid={`user-lang-${lang}`}
                    />
                    <span className="capitalize font-medium text-slate-700">{lang}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 leading-snug">
              Agents flagged Arabic receive the Arabic-only lead reassignment queue. English is the default pool.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>
              Workstation type
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-normal ml-1.5">optional</span>
            </Label>
            <Select value={form.workstation_kind} onValueChange={(v) => setForm({ ...form, workstation_kind: v })}>
              <SelectTrigger data-testid="user-workstation-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None / general</SelectItem>
                <SelectItem value="phone_agent">Phone agent — defaults to Inbound</SelectItem>
                <SelectItem value="backoffice">Backoffice ops — defaults to Service Call</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 leading-snug">
              Smart-default for new tickets. Phone agents auto-highlight <span className="text-emerald-700 font-medium">Inbound</span>; backoffice ops auto-highlight <span className="text-violet-700 font-medium">Service Call</span>.
            </p>
          </div>
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>
                Start from preset
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-normal ml-1.5">optional</span>
              </Label>
              <Select value={form.preset_id || "none"} onValueChange={(v) => setForm({ ...form, preset_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-preset-select"><SelectValue placeholder="Role defaults" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Role defaults (no preset)</SelectItem>
                  {presets.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.keys.length})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 leading-snug">
                Applies the preset's permissions as a one-time starting point. You can fine-tune later from the user's permission overrides.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="user-submit-btn">
            {submitting ? "Saving..." : (isEdit ? "Save changes" : "Send invite")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
